import { useMemo, useCallback } from "react";

/*
 * Estimation dropdown options sourced from the whole Spec Library.
 *
 * The Spec Library is still built exactly as before. The difference is that
 * Estimation no longer waits for a row to be checked (`select === true`) to
 * see it — every combination row is an option here, and the user picks what
 * they need directly in the Estimation columns.
 */

const toStr = (value) => String(value ?? "").trim();

const normKey = (value) => toStr(value).toLowerCase().replace(/\s+/g, "");

const uniqSorted = (values) =>
  Array.from(new Set((values || []).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );

/*
 * Structural's cascade. There is no Schedule/Rating or Ends here — those are
 * Piping fields. Spec comes first because the derived-field lookup is keyed on
 * `structural_spec_<Spec>`, so a row needs a Spec before UnitWeight,
 * UnitSA, MaterialRate or FabRate can resolve.
 */
export const CASCADE_FIELDS = ["Spec", "Components", "Size", "Material"];

/*
 * Copied onto the Estimation row once a spec row resolves. Deliberately
 * excludes UnitWeight / UnitSA / MaterialRate / FabRate: those are computed by
 * updateDerivedFields from the DB weight tables, and duplicating them here
 * would create a second source of truth.
 */
export const AUTOFILL_FIELDS = [
  "Material",
  "Material Description",
  "Requirements",
];

/*
 * allCombinationTable is the only source. The context regenerates it whenever
 * the spec selection changes, so it is populated regardless of which tab the
 * user opened.
 *
 * specTable is deliberately not used as a fallback: its Size column holds a
 * *range* ("1/2--4"), not the discrete sizes the dropdowns must offer. Falling
 * back to it put raw ranges in the Size dropdown.
 */
export function buildSpecRows(allCombinationTable) {
  return (Array.isArray(allCombinationTable) ? allCombinationTable : []).filter(
    (row) => row && !row.isSummary,
  );
}

/*
 * Rows still matching every already-chosen field above `stopAtField`. Passing
 * no `stopAtField` filters on the full cascade.
 */
export function matchRows(specRows, rowValues, stopAtField) {
  const stopIndex =
    stopAtField === undefined
      ? CASCADE_FIELDS.length
      : CASCADE_FIELDS.indexOf(stopAtField);

  const activeFields = CASCADE_FIELDS.slice(
    0,
    stopIndex === -1 ? CASCADE_FIELDS.length : stopIndex,
  );

  return (specRows || []).filter((specRow) =>
    activeFields.every((field) => {
      const chosen = toStr(rowValues?.[field]);
      if (!chosen) return true;
      return normKey(specRow?.[field]) === normKey(chosen);
    }),
  );
}

/*
 * Options for one column, narrowed by every column before it.
 *
 * A column offers nothing until every column before it in the cascade has been
 * picked — Spec first, then Components, then Size, then Material. This mirrors
 * Piping: without the gate, matchRows ignores blank fields, so an empty Spec
 * would still list every Component in the library and the user could fill the
 * row out of order.
 */
export function optionsFor(specRows, field, rowValues) {
  const fieldIndex = CASCADE_FIELDS.indexOf(field);

  if (fieldIndex > 0) {
    const priorFields = CASCADE_FIELDS.slice(0, fieldIndex);
    if (priorFields.some((prior) => !toStr(rowValues?.[prior]))) return [];
  }

  return uniqSorted(
    matchRows(specRows, rowValues, field).map((row) => toStr(row?.[field])),
  );
}

/*
 * The single spec row a selection resolves to, or null while ambiguous.
 * Ambiguity is judged on whether candidates agree on the autofilled values
 * rather than on row count — otherwise duplicate combination rows, which are
 * common, would never resolve.
 */
export function resolveRow(specRows, rowValues) {
  /*
   * Every cascade field must be filled. matchRows ignores empty fields, so a
   * partially filled row can otherwise match a single spec row by accident —
   * picking a Component with only one size would autofill a Size the user
   * never chose, and leave the row flagged complete with an empty Size.
   */
  const fullyChosen = CASCADE_FIELDS.every((field) =>
    Boolean(toStr(rowValues?.[field])),
  );
  if (!fullyChosen) return null;

  const matches = matchRows(specRows, rowValues);
  if (!matches.length) return null;
  if (matches.length === 1) return matches[0];

  const [first, ...rest] = matches;

  const allAgree = rest.every((row) =>
    AUTOFILL_FIELDS.every(
      (field) => normKey(row?.[field]) === normKey(first?.[field]),
    ),
  );

  return allAgree ? first : null;
}

/*
 * Apply a dropdown selection: set the chosen field, clear the fields below it
 * in the cascade (their old values may no longer be valid), then autofill from
 * the resolved spec row.
 *
 * `select` and `__specRow` are owned here so a single code path decides whether
 * a row is complete. A row that stops resolving — because an edit cleared the
 * fields below it — must lose its flag, otherwise a stale flag would ship an
 * incomplete row downstream.
 */
export function selectInto(specRows, row, field, value) {
  const next = { ...row, [field]: value };

  const fieldIndex = CASCADE_FIELDS.indexOf(field);
  if (fieldIndex !== -1) {
    CASCADE_FIELDS.slice(fieldIndex + 1).forEach((laterField) => {
      next[laterField] = "";
    });
  }

  /*
   * Fill any remaining field that has only one possible value. Without this the
   * user would have to open a dropdown that offers a single choice — picking a
   * Size that implies exactly one Material, for example. Stops at the first
   * genuinely ambiguous field so the user still makes every real decision.
   */
  CASCADE_FIELDS.forEach((cascadeField) => {
    if (toStr(next[cascadeField])) return;
    const options = optionsFor(specRows, cascadeField, next);
    if (options.length === 1) {
      next[cascadeField] = options[0];
    }
  });

  const specRow = resolveRow(specRows, next);

  if (!specRow) {
    /*
     * Material Description is autofilled rather than chosen, so cascade
     * clearing never reaches it. Left behind it would be stale — and since it
     * is the Summary grouping key, that stale value would flow into
     * generateSummary. Material is only cleared when the edit was above it in
     * the cascade; a Material the user picked themselves stays put.
     */
    next["Material Description"] = "";
    next.select = false;
    next.__specRow = null;
    return next;
  }

  AUTOFILL_FIELDS.forEach((autoField) => {
    const specValue = specRow[autoField];
    if (specValue !== undefined && specValue !== null && specValue !== "") {
      next[autoField] = specValue;
    }
  });

  /*
   * The Update / Update Assembly flow expects combination-row shape (TabName,
   * OriginalSizeRange, CategoryType…). The Estimation row keeps only a
   * reference to it.
   */
  next.__specRow = { ...specRow, select: true };
  next.select = true;

  return next;
}

export default function useSpecOptions(allCombinationTable) {
  const specRows = useMemo(
    () => buildSpecRows(allCombinationTable),
    [allCombinationTable],
  );

  const getOptions = useCallback(
    (field, rowValues) => optionsFor(specRows, field, rowValues),
    [specRows],
  );

  const resolveSpecRow = useCallback(
    (rowValues) => resolveRow(specRows, rowValues),
    [specRows],
  );

  const applySelection = useCallback(
    (row, field, value) => selectInto(specRows, row, field, value),
    [specRows],
  );

  return { getOptions, resolveSpecRow, applySelection, specRows };
}
